import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Send, Paperclip, X, FileText, Lock, Mail } from "lucide-react";
import { Spinner, Alert, TruncatedText } from "@/components/ui";
import { cn } from "@/lib/utils";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import {
  FILE_VALIDATION_PRESETS,
  formatFileSizeLimit,
  partitionValidatedFiles,
  toastRejectedFiles,
} from "@/lib/fileValidation";
import {
  CHAT_ICON_BUTTON_CLASS,
  CHAT_INPUT_BAR_CLASS,
  CHAT_SEND_BUTTON_CLASS,
  CHAT_TEXTAREA_CLASS,
} from "./theme";

function revokeAttachmentPreviews(attachments: ChatComposerAttachment[]): void {
  for (const attachment of attachments) {
    if (attachment.preview) {
      URL.revokeObjectURL(attachment.preview);
    }
  }
}

export interface ChatComposerAttachment {
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

export interface MessengerChatComposerProps {
  onSend: (
    message: string,
    attachments: ChatComposerAttachment[],
    isInternalNote: boolean,
  ) => Promise<void>;
  isSending: boolean;
  showInternalNoteToggle?: boolean;
  placeholder?: string;
  internalNotePlaceholder?: string;
}

export function MessengerChatComposer({
  onSend,
  isSending,
  showInternalNoteToggle = false,
  placeholder,
  internalNotePlaceholder,
}: MessengerChatComposerProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("support");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<ChatComposerAttachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const [isInternalNote, setIsInternalNote] = useState(false);

  const resolvedPlaceholder =
    placeholder ?? t("messageInput.defaultPlaceholder");
  const resolvedInternalPlaceholder =
    internalNotePlaceholder ?? t("messageInput.internalNotePlaceholder");

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const { accepted, rejected } = partitionValidatedFiles(
      files,
      FILE_VALIDATION_PRESETS.supportAttachment,
    );

    toastRejectedFiles(rejected, {
      tooLarge: (file, maxSize) =>
        t("messageInput.fileTooLarge", {
          name: file.name,
          max: formatFileSizeLimit(maxSize),
        }),
      unsupported: (file) =>
        t("messageInput.fileUnsupported", { name: file.name }),
    });

    const newAttachments: ChatComposerAttachment[] = accepted.map(
      ({ file }) => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : undefined,
      }),
    );
    if (newAttachments.length === 0) {
      e.target.value = "";
      return;
    }
    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const next = [...prev];
      if (next[index].preview) {
        URL.revokeObjectURL(next[index].preview!);
      }
      next.splice(index, 1);
      return next;
    });
  };

  const handleSend = async () => {
    if (!message.trim() && attachments.length === 0) return;
    await onSend(message.trim(), attachments, isInternalNote);
    revokeAttachmentPreviews(attachments);
    setMessage("");
    setAttachments([]);
    setIsInternalNote(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [message]);

  useEffect(() => {
    return () => {
      revokeAttachmentPreviews(attachmentsRef.current);
    };
  }, []);

  const canSend =
    !isSending && (message.trim().length > 0 || attachments.length > 0);

  return (
    <div className="shrink-0">
      {showInternalNoteToggle ? (
        <div className="flex border-t border-surface-200 bg-surface-0 px-3 pt-2.5">
          <div
            className="inline-flex w-full rounded-lg border border-surface-200 bg-surface-100/70 p-0.5 sm:w-auto"
            role="group"
            aria-label={t("messageInput.modeLabel")}
          >
            <button
              type="button"
              onClick={() => setIsInternalNote(false)}
              aria-pressed={!isInternalNote}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors sm:flex-none",
                !isInternalNote
                  ? "bg-surface-0 text-primary-700 shadow-xs ring-1 ring-surface-200 dark:bg-surface-200 dark:ring-surface-300/60"
                  : "text-surface-500 hover:text-surface-900",
              )}
            >
              <Mail className="h-3.5 w-3.5" />
              {t("messageInput.replyToPartner")}
            </button>
            <button
              type="button"
              onClick={() => setIsInternalNote(true)}
              aria-pressed={isInternalNote}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors sm:flex-none",
                isInternalNote
                  ? "bg-surface-0 text-warning-800 shadow-xs ring-1 ring-surface-200 dark:bg-surface-200 dark:ring-surface-300/60"
                  : "text-surface-500 hover:text-surface-900",
              )}
            >
              <Lock className="h-3.5 w-3.5" />
              {t("messageInput.internalNote")}
            </button>
          </div>
        </div>
      ) : null}

      {showInternalNoteToggle && isInternalNote ? (
        <Alert
          variant="warning"
          icon={Lock}
          className="rounded-none border-x-0 border-b-0 px-4 py-2 text-xs"
        >
          {t("messageInput.internalNoteWarning")}
        </Alert>
      ) : null}

      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-t border-surface-200 bg-surface-0 px-3 py-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center gap-2 rounded-lg bg-surface-50 py-1 pl-1 pr-2 ring-1 ring-inset ring-surface-200"
            >
              {attachment.preview ? (
                <img
                  src={attachment.preview}
                  alt={attachment.name}
                  className="h-7 w-7 rounded-md object-cover"
                />
              ) : (
                <FileText className="h-4 w-4 text-surface-400" />
              )}
              <TruncatedText className="max-w-[100px] text-xs text-surface-700">
                {attachment.name}
              </TruncatedText>
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                className="text-surface-400 hover:text-error-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className={CHAT_INPUT_BAR_CLASS}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept={FILE_ACCEPT.supportAttachment}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
          aria-label={t("messageInput.attach")}
          className={CHAT_ICON_BUTTON_CLASS}
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            isInternalNote ? resolvedInternalPlaceholder : resolvedPlaceholder
          }
          rows={1}
          disabled={isSending}
          onKeyDown={handleKeyDown}
          className={cn(
            CHAT_TEXTAREA_CLASS,
            showInternalNoteToggle &&
              isInternalNote &&
              "border-warning-300 bg-warning-50 focus:border-warning-500 focus:ring-warning-500/20",
          )}
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label={
            showInternalNoteToggle && isInternalNote
              ? t("messageInput.addNote")
              : t("messageInput.send")
          }
          className={cn(
            CHAT_SEND_BUTTON_CLASS,
            showInternalNoteToggle && isInternalNote
              ? "bg-warning-fill hover:bg-warning-fill-strong"
              : "bg-primary-fill hover:bg-primary-fill-strong",
          )}
        >
          {isSending ? (
            <Spinner size="sm" variant="inverse" loadingLabel={tCommon("loading")} />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
